import { useRef, useEffect } from 'react';
import { Marker } from 'wavesurfer.js/src/plugin/markers';

import { StoryConfig } from 'services/story.service';
import { RootState } from 'store/store';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { handleStoryConfig } from 'components/app/editor/_mediator/editor.slice';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import {
  getMarkerPositionIdentifiers,
  getMarkersEnabledRange,
  getRangeMarkerTimes,
  getRangeMarkers,
  setRangeClipsClass,
  setRangeRegionsClass,
} from './range-markers.helper';

import audioTrackStyles from 'components/app/audio-track/_audio-track.module.scss';

type TMarkerPosition = 'top' | 'bottom';

interface IMarker extends Marker {
  el: HTMLElement;
}

export const useRangeMarkersSb = (ws: WaveSurfer) => {
  const { wsGetCursorFraction } = useAudioTrackContext();
  const markerMouseDownTypeRef = useRef<TMarkerPosition | null>(null);
  const { markers, storyConfig } = useAppSelector((state) => state.editor);
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const onMarkerMouseDownEvent = (position: TMarkerPosition) => {
    return (markerMouseDownTypeRef.current = position);
  };

  const onMarkerMouseUpEvent = (event: MouseEvent) => {
    if (!markerMouseDownTypeRef.current) return;
    const { selectedStory, storyConfig, markers, storyBoard } = selectData(store.getState());
    if (!ws || !markers || !storyConfig || !selectedStory) return;

    const { markerType, isPositionInvalid, closeToEndEdge, closeToStartEdge, appropriateRegion } =
      getMarkerPositionIdentifiers({
        config: storyConfig,
        regionsTimecodes: markers,
        fraction: wsGetCursorFraction(event.clientX),
        markerType: markerMouseDownTypeRef.current === 'top' ? 'start' : 'end',
        duration: ws.getDuration(),
      });

    if (isPositionInvalid) {
      setRangeMarkers(storyConfig, markers);
      setRangeClipsClass({
        storyBoard,
        start: storyConfig.startMarkerId,
        end: storyConfig.finishMarkerId,
        markers,
        markerType,
        closeToEndEdge,
        closeToStartEdge,
      });
    } else {
      dispatch(
        handleStoryConfig({
          data: {
            storyConfig: {
              finishMarkerId:
                markerType === 'end'
                  ? closeToEndEdge
                    ? null
                    : markers.indexOf(appropriateRegion!)
                  : storyConfig.finishMarkerId,
              startMarkerId:
                markerType === 'start'
                  ? closeToStartEdge
                    ? null
                    : markers.indexOf(appropriateRegion!)
                  : storyConfig.startMarkerId,
            },
          },
          backupParams: { title: 'Range Markers' },
        })
      );
    }

    markerMouseDownTypeRef.current = null;
  };

  const setRangeMarkers = (config: StoryConfig, markers: number[] | null) => {
    if (!markers || !config) return;

    ws.clearMarkers();
    const { startTime, endTime } = getRangeMarkerTimes({
      startMarkerId: config.startMarkerId,
      finishMarkerId: config.finishMarkerId,
      duration: ws.getDuration(),
      markers,
    });

    getRangeMarkers(startTime, endTime, true).forEach((marker) => {
      const newMarker: IMarker = ws.addMarker(marker);
      newMarker.el.classList.remove(audioTrackStyles['marker-fixed']);
      newMarker.el.addEventListener('mousedown', () => onMarkerMouseDownEvent(marker.position as TMarkerPosition));

      // Add data-intro attributes for tutorial highlighting
      if (marker.position === 'top') {
        newMarker.el.setAttribute('data-intro', String(IntroKeys.startRangeMarker));
      } else if (marker.position === 'bottom') {
        newMarker.el.setAttribute('data-intro', String(IntroKeys.endRangeMarker));
      }
    });
  };

  const onMarkerDragEvent = (event: MouseEvent) => {
    if (!markerMouseDownTypeRef.current) return;
    const { storyBoard, markers, storyConfig } = selectData(store.getState());
    if (!storyConfig || !markers) return;

    const { markerType, appropriateRegionIndex, closeToEndEdge, closeToStartEdge } = getMarkerPositionIdentifiers({
      config: storyConfig,
      regionsTimecodes: markers,
      fraction: wsGetCursorFraction(event.clientX),
      markerType: markerMouseDownTypeRef.current === 'top' ? 'start' : 'end',
      duration: ws.getDuration(),
    });

    const start = markerType === 'start' ? appropriateRegionIndex : storyConfig.startMarkerId;
    const end = markerType === 'end' ? appropriateRegionIndex : storyConfig.finishMarkerId;
    const dif = (end ?? markers.length) - (start ?? 0);

    if (dif <= 0) return;

    setRangeRegionsClass(
      Array.from({ length: dif + (closeToEndEdge ? 1 : 0) }, (_, i) => i + (start ?? 0)),
      ws
    );
    setRangeClipsClass({ storyBoard, start, end, markers, markerType, closeToEndEdge, closeToStartEdge });
  };

  useEffect(() => {
    const { storyConfig, markers } = selectData(store.getState());

    if (ws && storyConfig) {
      setRangeMarkers(storyConfig, markers);
      setRangeRegionsClass(getMarkersEnabledRange(storyConfig, markers?.length ?? 0, 'marker'), ws);
    }
  }, [markers, storyConfig?.startMarkerId, storyConfig?.finishMarkerId]);

  useEffect(() => {
    document.addEventListener('mouseup', onMarkerMouseUpEvent);
    document.addEventListener('mousemove', onMarkerDragEvent);

    return () => {
      ws.markers?.markers?.forEach((marker) => {
        (marker as IMarker).el.removeEventListener('mousedown', () => onMarkerMouseDownEvent(marker.position));
      });
      ws.clearMarkers();
      document.removeEventListener('mouseup', onMarkerMouseUpEvent);
      document.removeEventListener('mousemove', onMarkerDragEvent);
    };
  }, []);
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { selectedMarkers, storyBoard, markers, storyConfig } = state.editor;
  const { selectedStory } = state.projects;
  return {
    markers,
    selectedStory,
    selectedMarkers,
    storyConfig,
    storyBoard,
  };
};
